import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {
    Card,
    Typography,
    Descriptions,
    Button,
    Modal,
    Space,
    Form,
    Select,
    DatePicker,
    TimePicker,
    message,
    Row,
    Col,
    Input
} from "antd";
import {CheckCircleTwoTone, CloseCircleTwoTone, LeftOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import api from "../../api";
import {boldLabelStyle, maxW700, NoWrap} from "../../styles/styles";

const {Title} = Typography;
const {Option} = Select;
dayjs.extend(isSameOrAfter);

const HotelDetailPage: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [hotel, setHotel] = useState<any>(null);
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const checkinDate = Form.useWatch("checkin_date", form);
    const specialRequests = Form.useWatch("special_requests", form) || [];
    const [nightPayments, setNightPayments] = useState<{ date: string; payment: string }[]>([]);

    useEffect(() => {
        fetchHotel();
    }, [id]);

    useEffect(() => {
        if (hotel) {
            // Convert date strings into dayjs for DatePickers
            form.setFieldsValue({
                ...hotel,
                rooms: 1,
                room_types: hotel.room_types?.[0] || undefined,
                checkin_date: hotel.checkin_date ? dayjs(hotel.checkin_date) : null,
                checkout_date: hotel.checkout_date ? dayjs(hotel.checkout_date) : null,
                early_checkin_time: hotel.early_checkin_time ? dayjs(hotel.early_checkin_time, "HH:mm:ss") : null,
                late_checkout_time: hotel.late_checkout_time ? dayjs(hotel.late_checkout_time, "HH:mm:ss") : null,
                per_night_payments: hotel.per_night_payments || {},
            });

            // Build UI state for nights
            if (hotel.checkin_date && hotel.checkout_date) {
                const nights: { date: string; payment: string }[] = [];
                let current = dayjs(hotel.checkin_date);
                const end = dayjs(hotel.checkout_date);

                while (current.isBefore(end, "day")) {
                    nights.push({
                        date: current.format("YYYY-MM-DD"),
                        payment: hotel.per_night_payments?.[current.format("YYYY-MM-DD")] ||
                            (nights.length === 0 ? "IM Comp / Guest Pay" : ""),
                    });
                    current = current.add(1, "day");
                }
                setNightPayments(nights);
            }
        }
    }, [hotel, form]);

    const fetchHotel = async () => {
        try {
            const res = await api.get(`/cc/hotel/${id}/`);
            setHotel(res.data);
            form.setFieldsValue({
                ...res.data,
                splash_bay: false,
                checkin_date: res.data.checkin_date ? dayjs(res.data.checkin_date) : null,
                checkout_date: res.data.checkout_date ? dayjs(res.data.checkout_date) : null,
                early_checkin_time: res.data.early_checkin_time ? dayjs(res.data.early_checkin_time, "HH:mm:ss") : null,
                late_checkout_time: res.data.late_checkout_time ? dayjs(res.data.late_checkout_time, "HH:mm:ss") : null
            });
        } catch {
            message.error("Failed to fetch hotel reservation");
        }
    };

    const handleUpdate = async (values: any) => {
        try {
            const payload = {
                ...values,
                rooms: 1,
                room_types: values.room_types ? [values.room_types] : [],
                splash_bay: false,
                payment_type: "IM COMP",
                customer_id: hotel.customer.id,
                checkin_date: values.checkin_date?.format("YYYY-MM-DD"),
                checkout_date: values.checkout_date?.format("YYYY-MM-DD"),
                early_checkin_time: values.early_checkin_time?.format("HH:mm:ss") || null,
                late_checkout_time: values.late_checkout_time?.format("HH:mm:ss") || null,
                per_night_payments: values.per_night_payments,
            };
            await api.put(`/cc/hotel/${id}/`, payload);
            message.success("Hotel updated");
            setIsModalVisible(false);
            fetchHotel();
        } catch (err) {
            message.error("Update failed");
        }
    };

    const handleDelete = async () => {
        Modal.confirm({
            title: "Are you sure you want to delete this reservation?",
            content: "This action cannot be undone.",
            okText: "Yes, delete it",
            okType: "danger",
            cancelText: "Cancel",
            centered: true,
            onOk: async () => {
                try {
                    await api.delete(`/cc/hotel/${id}/`);
                    message.success("Hotel reservation deleted");
                    navigate("/cc/hotel");  // or wherever your list page is
                } catch {
                    message.error("Failed to delete hotel reservation");
                }
            },
        });
    };

    if (!hotel) return null;

    return (
        <div style={{padding: 24}}>
            <Button icon={<LeftOutlined/>} onClick={() => navigate(-1)} style={{marginBottom: 24}}>
                Back
            </Button>

            <Title level={3}>Hotel Reservation Details</Title>

            <Card
                className="rounded-2xl shadow-sm"
                title={
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={5} style={{margin: 0}}>
                                Casino Club Hotel Reservation Overview
                            </Title>
                        </Col>
                        <Col>
                            <Space>
                                <Button danger onClick={handleDelete} className="rounded-md">
                                    Delete
                                </Button>
                                <Button type="primary" onClick={() => setIsModalVisible(true)} className="rounded-md">
                                    Edit
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                }
                style={{marginTop: 16}}
            >
                <Descriptions bordered column={1} style={maxW700}>
                    <Descriptions.Item label="Customer" labelStyle={boldLabelStyle}>
                        <NoWrap>{hotel.customer?.first_name} {hotel.customer?.last_name}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item label="Check-in Date"
                                       labelStyle={boldLabelStyle}><NoWrap>{hotel.checkin_date}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Check-out Date"
                                       labelStyle={boldLabelStyle}><NoWrap>{hotel.checkout_date}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Rooms"
                                       labelStyle={boldLabelStyle}><NoWrap>{hotel.rooms}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Tower"
                                       labelStyle={boldLabelStyle}><NoWrap>{hotel.tower}</NoWrap></Descriptions.Item>

                    <Descriptions.Item
                        label="Payment Type (Per Night)"
                        labelStyle={boldLabelStyle}
                        span={3}
                    >
                        {hotel.per_night_payments && Object.keys(hotel.per_night_payments).length > 0 ? (
                            <Space direction="vertical" style={{width: "100%"}}>
                                {Object.entries(hotel.per_night_payments as Record<string, string>).map(
                                    ([date, payment], idx) => (
                                        <Typography.Text key={date}>
                                            Night {idx + 1} ({dayjs(date).format("MMM D, YYYY")}): {payment}
                                        </Typography.Text>
                                    )
                                )}
                            </Space>
                        ) : hotel.payment_type ? (
                            <Typography.Text>{hotel.payment_type}</Typography.Text>
                        ) : (
                            <NoWrap>-</NoWrap>
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Confirmation Number" labelStyle={boldLabelStyle}>
                        <NoWrap>{hotel.confirmation_number || "-"}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item label="Notes" labelStyle={boldLabelStyle}>
                        <NoWrap>{hotel.notes || "-"}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item
                        label="Special Requests"
                        labelStyle={boldLabelStyle}><NoWrap>{hotel.special_requests?.join(", ")}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Early Check-in Time" labelStyle={boldLabelStyle}>
                        <NoWrap>{hotel.early_checkin_time?.slice(0, 5) || "-"}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item label="Late Checkout Time" labelStyle={boldLabelStyle}>
                        <NoWrap>{hotel.late_checkout_time?.slice(0, 5) || "-"}</NoWrap>
                    </Descriptions.Item>
                    {/*<Descriptions.Item label="Splash Bay" labelStyle={boldLabelStyle}>*/}
                    {/*    <NoWrap>*/}
                    {/*        {hotel.splash_bay ? (*/}
                    {/*            <CheckCircleTwoTone twoToneColor="#52c41a"/>*/}
                    {/*        ) : (*/}
                    {/*            <CloseCircleTwoTone twoToneColor="#ff4d4f"/>*/}
                    {/*        )}*/}
                    {/*    </NoWrap>*/}
                    {/*</Descriptions.Item>*/}
                </Descriptions>
            </Card>

            <Modal
                title="Edit Hotel Reservation"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                width={700}
                centered
                okButtonProps={{className: "rounded-md"}}
                cancelButtonProps={{className: "rounded-md"}}
                bodyStyle={{maxHeight: "70vh", overflowY: "auto"}}
            >
                <Form layout="vertical" form={form} onFinish={handleUpdate} initialValues={{rooms: 1}}>
                    <Form.Item name="checkin_date" label="Check-in Date" rules={[{required: true}]}>
                        <DatePicker style={{width: "100%"}}/>
                    </Form.Item>

                    <Form.Item
                        name="checkout_date"
                        label="Check-out Date"
                        rules={[
                            {required: true},
                            {
                                validator: (_, value) => {
                                    if (!value || !dayjs.isDayjs(value)) return Promise.resolve();
                                    if (!checkinDate || value.isAfter(checkinDate, "day")) return Promise.resolve();
                                    return Promise.reject(new Error("Checkout date must be after check-in date"));
                                },
                            },
                        ]}
                    >
                        <DatePicker
                            style={{width: "100%"}}
                            disabledDate={(current) =>
                                current &&
                                (current < dayjs().startOf("day") ||
                                    (checkinDate && current.isBefore(checkinDate, "day")))
                            }
                        />
                    </Form.Item>

                    {nightPayments.length > 0 && (
                        <div style={{marginBottom: 16}}>
                            <Typography.Text strong>Payment Type Per Night</Typography.Text>
                            {nightPayments.map((n, idx) => (
                                <Form.Item
                                    key={n.date}
                                    label={dayjs(n.date).format("MMM D, YYYY")}
                                    name={["per_night_payments", n.date]}
                                    initialValue={n.payment}
                                    rules={[{required: true, message: "Please select a payment type"}]}
                                >
                                    <Select disabled={idx === 0}>
                                        <Select.Option value="IM Comp / Guest Pay">IM Comp / Guest Pay</Select.Option>
                                        <Select.Option value="IM Comp / Casino Club PM">IM Comp / Casino Club
                                            PM</Select.Option>
                                        <Select.Option value="Guest Pay">Guest Pay</Select.Option>
                                        <Select.Option value="Casino Club PM">Casino Club PM</Select.Option>
                                        <Select.Option value="DAIA PM">DAIA PM</Select.Option>
                                    </Select>
                                </Form.Item>
                            ))}
                        </div>
                    )}

                    {/*<Form.Item name="rooms" label="Rooms">*/}
                    {/*    <Select>*/}
                    {/*        {[1, 2, 3, 4].map((n) => (*/}
                    {/*            <Option key={n} value={n}>*/}
                    {/*                {n}*/}
                    {/*            </Option>*/}
                    {/*        ))}*/}
                    {/*    </Select>*/}
                    {/*</Form.Item>*/}

                    <Form.Item name="tower" label="Tower">
                        <Select>
                            <Option value="Sun">Sun</Option>
                            <Option value="Ocean">Ocean</Option>
                            <Option value="Forest">Forest</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="room_types" label="Room Type">
                        <Select>
                            <Option value="Deluxe Double">Deluxe Double</Option>
                            <Option value="Deluxe King">Deluxe King</Option>
                            <Option value="Studio Suite">Studio Suite</Option>
                            <Option value="Sun Suite">Sun Suite</Option>
                            <Option value="Inspire Suite">Inspire Suite</Option>
                            <Option value="Forest Suite">Forest Suite</Option>
                            <Option value="Ocean Suite">Ocean Suite</Option>
                            <Option value="Villa">Villa</Option>
                        </Select>
                    </Form.Item>

                    {/*<Form.Item name="payment_type" label="Payment Type">*/}
                    {/*    <Select>*/}
                    {/*        <Option value="IM Comp / Guest Pay">IM Comp / Guest Pay</Option>*/}
                    {/*        <Option value="IM Comp / Casino Club PM">IM Comp / Casino Club*/}
                    {/*            PM</Option>*/}
                    {/*        <Option value="Guest Pay">Guest Pay</Option>*/}
                    {/*        <Option value="Casino Club PM">Casino Club PM</Option>*/}
                    {/*        <Option value="DAIA PM">DAIA PM</Option>*/}
                    {/*    </Select>*/}
                    {/*</Form.Item>*/}

                    <Form.Item
                        name="confirmation_number"
                        label="Confirmation Number"
                        rules={[
                            {required: true, message: "Confirmation number is required"},
                            {len: 6, message: "Confirmation number must be exactly 6 characters"},
                        ]}
                    >
                        <Input maxLength={6}/>
                    </Form.Item>

                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea rows={3}/>
                    </Form.Item>

                    <Form.Item name="special_requests" label="Special Requests">
                        <Select mode="multiple">
                            <Option value="Early Check-in">Early Check-in</Option>
                            <Option value="Late Checkout">Late Checkout</Option>
                            <Option value="Extra Bed">Extra Bed</Option>
                        </Select>
                    </Form.Item>

                    {specialRequests.includes("Early Check-in") && (
                        <Form.Item name="early_checkin_time" label="Early Check-in Time">
                            <TimePicker format="HH:mm" style={{width: "100%"}}/>
                        </Form.Item>
                    )}
                    {specialRequests.includes("Late Checkout") && (
                        <Form.Item name="late_checkout_time" label="Late Checkout Time">
                            <TimePicker format="HH:mm" style={{width: "100%"}}/>
                        </Form.Item>
                    )}

                    {/*<Form.Item name="splash_bay" label="Splash Bay">*/}
                    {/*    <Select>*/}
                    {/*        <Option value={true}>Yes</Option>*/}
                    {/*        <Option value={false}>No</Option>*/}
                    {/*    </Select>*/}
                    {/*</Form.Item>*/}
                </Form>
            </Modal>
        </div>
    );
};

export default HotelDetailPage;